import { Link } from "react-router-dom";
import { useState } from "react";
import BudLogo from "@/components/BudLogo";
import { MapPin, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

const NO_THCA_STATES = ["Arkansas", "Idaho", "Minnesota", "Oregon", "Rhode Island", "Vermont"];
const NO_DELTA8_STATES = ["Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Kentucky", "Idaho", "Iowa", "Michigan", "Mississippi", "Montana", "New York", "Nevada", "North Dakota", "Oregon", "Rhode Island", "Utah", "Vermont", "Washington"];

export default function Footer() {
  const [open, setOpen] = useState(false);
  return (
    <footer data-testid="footer" className="surface-ink border-t-2 border-[#0B0F0C] mt-16">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-[#F5F0DC]">
        <div className="lg:col-span-2">
          <Link to="/" className="flex items-center gap-2.5 mb-4 group">
            <BudLogo size="md" tone="dark" spin={false} />
            <span className="font-display text-2xl font-bold">X-Seed</span>
          </Link>
          <p className="text-sm text-[#F5F0DC]/70 max-w-md leading-relaxed">
            Find the highest-quality cannabis at the best price. Real deals, real commissions, real fast.
          </p>
          <div className="mt-5 flex items-start gap-2 text-xs text-[#F5F0DC]/85">
            <MapPin size={14} strokeWidth={2.4} className="mt-0.5 flex-shrink-0 text-[#15FF73]" />
            <div>
              <div className="font-bold">X-Seed HQ</div>
              <div className="text-[#F5F0DC]/65">4201 FM 1960 Rd West, Houston, TX 77068</div>
              <a href="https://www.xseed.guru" className="text-[#15FF73] hover:underline font-mono-x font-bold uppercase tracking-widest text-[10px]">www.xseed.guru</a>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-2 text-xs font-mono-x font-bold uppercase tracking-widest">
            <span className="pulse-dot" /> 21+ · Compliant · Multi-state
          </div>
        </div>
        <div>
          <div className="label-overline mb-3 text-[#15FF73]">Browse</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/discover" className="hover:text-[#15FF73]">Discover</Link></li>
            <li><Link to="/deals" className="hover:text-[#15FF73]">Deals</Link></li>
            <li><Link to="/concierge" className="hover:text-[#15FF73]">Bud</Link></li>
            <li><Link to="/community" className="hover:text-[#15FF73]">Community</Link></li>
            <li><Link to="/about" className="hover:text-[#15FF73]">About</Link></li>
          </ul>
        </div>
        <div>
          <div className="label-overline mb-3 text-[#FF1A8C]">For Stores</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/pitch" className="hover:text-[#15FF73]">Pitch deck</Link></li>
            <li><Link to="/billing" className="hover:text-[#15FF73]">Pricing</Link></li>
            <li><Link to="/auth?mode=register&role=dispensary" className="hover:text-[#15FF73]">List your shop</Link></li>
            <li><Link to="/dashboard" className="hover:text-[#15FF73]">Dashboard</Link></li>
            <li><Link to="/scan" className="hover:text-[#15FF73]">Scanner</Link></li>
          </ul>
        </div>
      </div>

      {/* === Compliance / FDA disclaimer === */}
      <div className="border-t border-[#F5F0DC]/10 bg-black/30">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 text-[11px] leading-relaxed text-[#F5F0DC]/55">
          <button
            data-testid="legal-toggle"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-2 text-[10px] font-mono-x font-bold uppercase tracking-widest text-[#FFE74C] hover:text-[#FFD500] mb-3"
          >
            <AlertTriangle size={12} strokeWidth={2.4} />
            Legal & Compliance Disclaimer
            {open ? <ChevronUp size={12} strokeWidth={2.4} /> : <ChevronDown size={12} strokeWidth={2.4} />}
          </button>

          {/* Always-visible critical line */}
          <p className="font-bold text-[#F5F0DC]/85 mb-2" data-testid="legal-fda-short">
            These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure or prevent any disease. Must be 21+ to purchase.
          </p>

          {open && (
            <div className="space-y-3 mt-3" data-testid="legal-full">
              <p>
                This product is not intended for children, or pregnant or lactating women. Consult with a physician before use if you have a serious medical condition or use prescription medications. A Doctor's advice should be sought before using this and any dietary supplement product. All trademarks and copyrights are property of their respective owners and are not affiliated with nor do they endorse this product. By using this site, you agree to follow the Privacy Policy and all Terms &amp; Conditions printed on this site. Void Where Prohibited by Law. Products on this site contain less than 0.3% Δ9-THC.
              </p>

              <div className="rounded-xl border border-[#F5F0DC]/20 p-3">
                <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest text-[#FF1A8C] mb-1">No THCA shipping</div>
                <p>THCA products are not available for shipment to the following states: {NO_THCA_STATES.join(", ")}.</p>
              </div>

              <div className="rounded-xl border border-[#F5F0DC]/20 p-3">
                <div className="text-[10px] font-mono-x font-bold uppercase tracking-widest text-[#FF1A8C] mb-1">No Delta-8 shipping</div>
                <p>Delta-8 products are not available for shipment to the following states: {NO_DELTA8_STATES.join(", ")}.</p>
              </div>

              <p className="text-[10px] font-mono-x uppercase tracking-widest opacity-70 pt-1">
                Mushroom &amp; herbal products sold on X-Seed contain only legal botanicals (Amanita Muscaria, functional mushrooms, kratom, kava, etc.). X-Seed does not sell psilocybin or any federally-scheduled controlled substance.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-[#F5F0DC]/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col sm:flex-row justify-between gap-3 text-[10px] text-[#F5F0DC]/50 font-mono-x uppercase tracking-widest">
          <div>© {new Date().getFullYear()} X-Seed · xseed.guru</div>
          <div>Cannabis is regulated. Comply with local law.</div>
        </div>
      </div>
    </footer>
  );
}
